import pluralize from 'pluralize';
import {
  isObject,
  isString,
  isArray,
  isUndefined,
} from 'lodash';

export function bmk(key: string, value: string): string {
  if (value === '') {
    return value; // :nothingtodohere:
  }

  // Don't try to format identifiers that merely *look* like numbers
  if (['zip', 'year_built', 'mls_number'].includes(key)) {
    return value;
  }

  let out = value; // *rolls up sleeves*

  // Integers only, please
  out = out.replace(/^([0-9]+)\.0+$/, '$1');

  // If the number is an even thousand, million, or billion (with or without commas), shorten it
  out = out.replace(/^([0-9]{1,3}),?000,?000,?000$/, '$1b');
  out = out.replace(/^([0-9]{1,3}),?000,?000$/, '$1m');
  out = out.replace(/^([0-9]{1,3}),?000$/, '$1k');

  // Not a nice round number?  We can at least add some commas to it while we're here...
  out = out.replace(/^([0-9]{1,3})([0-9]{3})([0-9]{3})([0-9]{3})$/, '$1,$2,$3,$4');
  out = out.replace(/^([0-9]{1,3})([0-9]{3})([0-9]{3})$/, '$1,$2,$3');
  out = out.replace(/^([0-9]{1,3})([0-9]{3})$/, '$1,$2');

  return out;
}

/*                     Start with a literal '['
 *                     | Capture everything up to ']' or '|' in group 1; this is the 'key'
 *                     | |        Start a non-capturing group
 *                     | |        |  Literal '|'; this delimits the 'key' from the 'label'
 *                     | |        |  | Capture everything up to ']' as group 2; this is the 'label'
 *                     | |        |  | |       Close the non-capturing group
 *                     | |        |  | |       | finish with a literal ']'
 *                     | |        |  | |       | |
 *                     | |        |  | |       | |  last but not least, make 'replace' act like 'replaceAll'
 *                     | |        |  | |       | |  |
 *                     V V        V  V V       V V  V */
const REPLACE_TOKEN = /\[([^\]|]+)(?:\|([^\]]+))?\]/g;

export async function replaceTemplate(
  template: string,
  values: Record<string, string>,
  lookup: object,
  override_lookup: Record<string, Record<string, string>>,
  markup: boolean,
): Promise<string> {
  const merged_lookup = {
    ...lookup,
    ...override_lookup,
  };

  function replacer(
    match: string,
    key: string,
    label: string,
    offset: number,
  ) {
    const initial = values[key] || rangeFromMinMax(values, key);
    const replaced = replaceInitial(key, initial, merged_lookup);
    const formatted = markup ? markThatShitUp(key, replaced, offset, match) : replaced;

    if (isMissing(formatted)) {
      return isMissing(label) ? key : match;
    }
    const suffix = suffixForLabel(formatted, label);
    return isMissing(suffix) ? formatted : `${formatted} ${suffix}`;
  }

  return newlinesGoBrrrrrrrrr(
    template.replace(REPLACE_TOKEN, replacer),
    markup,
  );
}

function isMissing(x: unknown) { // TODO: can TypeScript make this suck less?
  if (x === null) { return true; }
  if (isUndefined(x)) { return true; }
  if (isString(x)) { return x.trim() === ''; }
  return false;
}

function lookUpValue(
  merged_lookup: Record<string, string | number>,
  key: string,
  value: string | number,
) {
  const lkup = merged_lookup[key] || {};
  const val = lkup[value] || value;
  return bmk(key, val);
}

function makeSeries(words: string[]) {
  if (words.length === 1) { return words[0]; }
  if (words.length === 2) { return words.join(' and '); }

  const list = words.join(', ');
  const stil = rev_string(list); // 'list' reversed
  const dna_stil = stil.replace(' ,', ' dna ,'); // 'list_and' reversed
  return rev_string(dna_stil);
}

// it's not named `markUp` because that's too easy to get confused with the `markup` variable
function markThatShitUp(
  key: string,
  value: string | number,
  offset: number,
  match: string,
) {
  const i = offset;
  const j = offset + match.length;
  if (isMissing(value)) {
    return `<span class="not-found" data-start="${i}" data-stop="${j}">${match}</span>`;
  }
  return `<span data-field="${key}" data-start="${i}" data-stop="${j}">${value}</span>`;
}

function newlinesGoBrrrrrrrrr(text: string, markup: boolean) {
  return markup ? text.replace('\n', '<br>') : text;
}

function rangeFromMinMax(values: Record<string, string>, key: string) {
  const lo = parseInt(values[`${key}_min`], 10);
  const hi = parseInt(values[`${key}_max`], 10);
  // eslint-disable-next-line
  function have(maybe_int) { return !isNaN(maybe_int); }

  const min = have(lo) ? bmk(key, String(lo)) : '';
  const max = have(hi) ? bmk(key, String(hi)) : '';

  if (have(lo) && have(hi)) { return `${min}-${max}`; }
  if (have(lo)) { return `${min}+`; }
  if (have(hi)) { return `0-${max}`; }
  return '';
}

export function replaceInitial(
  key: string,
  initial: string | { data: string } | string[],
  merged_lookup: Record<string, string>,
): string { // TODO: Can TypeScript overload on arg type?
  if (isArray(initial)) {
    return makeSeries(initial.map(e => lookUpValue(merged_lookup, key, e)));
  }
  if (isObject(initial)) {
    return lookUpValue(merged_lookup, key, initial.data);
  }
  if (isString(initial)) {
    return lookUpValue(merged_lookup, key, initial);
  }
  return '';
}

// in which sam bombs his interview for a front-end dev position
function rev_string(s: string) {
  return s.split('').reverse().join('');
}

function suffixForLabel(formatted: string, label: string): string | null {
  if (isMissing(label)) { return null; }
  let n = parseInt(formatted, 10);
  // eslint-disable-next-line
  n = isNaN(n) ? 0 : n;
  return pluralize(label, n);
}

